import { AutocompleteOptions } from '@Components/Autocomplete';
import CommonConstants from '@Constants/common.constants';
import {
  ActiveSessionsQuery,
  CallLogReportQuery,
  EmailLogReportQuery,
  SessionsResponse,
  SessionsSummaryQuery,
} from '@Models/report.model';
import { UsersListRequestAndResponse } from '@Models/users.models';
import { VoiceProviderTypes } from '@Typings/global.types';
import { nextDayISOString } from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';

/** RETURNS SESSIONS API DATA IN DESIRED FORMAT */
export const getSessions = (
  sessions?: SessionsResponse[]
): AutocompleteOptions[] =>
  sessions?.map(
    (session: SessionsResponse): AutocompleteOptions => ({
      label: session.name,
      value: session.id,
    })
  ) || [];

/** RETURNS USERS API DATA IN DESIRED FORMAT */
export const getAccounts = (
  accounts?: UsersListRequestAndResponse['data']
): AutocompleteOptions[] =>
  accounts?.map((account) => ({
    label: `${account.accountId} - ${account.firstName} ${account.lastName}`,
    value: `${account.accountId}`,
  })) || [];

/** RETURNS SESSIONS RTK QUERY */
export const getSessionsRTKQuery = (
  sessions: AutocompleteOptions[]
): string[] => sessions.map((item) => item.value);

/** RETURNS USER ACCOUNTS RTK QUERY  */
export const getAccountsRTKQuery = (
  count?: number
): UsersListRequestAndResponse => ({
  searchTerm: '',
  sort: {
    field: CommonConstants.QUERY_PARAMS.USERS.SORT_BY.FULL_NAME,
    direction: CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC,
  },
  pagination: {
    pageNumber: CommonConstants.INITIAL_PAGE_NO,
    objPerPage: count || CommonConstants.INITIAL_ROWS_PER_PAGE,
  },
});

/**
 * RETURNS CALL LOG REPORT RTK QUERY
 * @param search - URL Query Params
 * @param isAdmin - (optional) boolean for Admin CallLog Report
 * @returns RTK Query Parameters for CallLog Report
 */
export const getCallLogReportRTKQuery = (
  search: string
): CallLogReportQuery => {
  const queries = query.parse(search);

  return {
    pageNo:
      (queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO] as number) - 1,
    pageSize: queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE] as number,
    userIds: queries[CommonConstants.QUERY_PARAMS.REPORT.USER_IDS] as string[],
    sessionId: queries[
      CommonConstants.QUERY_PARAMS.REPORT.SESSION_ID
    ] as string[],
    search: queries[CommonConstants.QUERY_PARAMS.COMMON.SEARCH] as string,
    listType: queries[
      CommonConstants.QUERY_PARAMS.COMMON.LIST_TYPE
    ] as string[],
    callStatus: queries[
      CommonConstants.QUERY_PARAMS.REPORT.CALL_STATUS
    ] as string[],
    disposition: queries[
      CommonConstants.QUERY_PARAMS.REPORT.DISPOSITION
    ] as string[],
    dateStart: queries[
      CommonConstants.QUERY_PARAMS.COMMON.DATE_START
    ] as string,
    dateEnd: nextDayISOString(
      queries[CommonConstants.QUERY_PARAMS.COMMON.DATE_END] as string
    ),
    timeZoneOffset: -new Date().getTimezoneOffset(), // we are sending a negative value of the offset as the BE expects the offset to be a certain way
    voiceProvider: queries[
      CommonConstants.QUERY_PARAMS.REPORT.VOICE_PROVIDER
    ] as VoiceProviderTypes,
  };
};

/**
 * RETURNS EMAIL LOG REPORT RTK QUERY
 * @param search - URL Query Params
 * @returns RTK Query Parameters for EmailLog Report
 */
export const getEmailLogReportRTKQuery = (
  search: string
): EmailLogReportQuery => {
  const queries = query.parse(search);

  return {
    pageNo:
      (queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO] as number) - 1,
    pageSize: queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE] as number,
    userIds: queries[CommonConstants.QUERY_PARAMS.REPORT.USER_IDS] as string[],
    search: queries[CommonConstants.QUERY_PARAMS.COMMON.SEARCH] as string,
    dateStart: queries[
      CommonConstants.QUERY_PARAMS.COMMON.DATE_START
    ] as string,
    dateEnd: nextDayISOString(
      queries[CommonConstants.QUERY_PARAMS.COMMON.DATE_END] as string
    ),
    timeZoneOffset: -new Date().getTimezoneOffset(), // we are sending a negative value of the offset as the BE expects the offset to be a certain way
    order: queries[CommonConstants.QUERY_PARAMS.COMMON.ORDER] as string,
    direction: queries[CommonConstants.QUERY_PARAMS.COMMON.DIRECTION] as string,
  };
};

/**
 * RETURNS SESSIONS SUMMARY RTK QUERY
 * @param search - URL Query Params
 * @param isAdmin - (optional) boolean for Admin Sessions Summary
 * @returns RTK Query Parameters for Sessions Summary
 */
export const getSessionSummaryRTKQuery = (
  search: string,
  isAdmin?: boolean
): SessionsSummaryQuery => {
  const queries = query.parse(search);

  return {
    sessionId: queries[
      CommonConstants.QUERY_PARAMS.REPORT.SESSION_ID
    ] as string[],
    search: queries[CommonConstants.QUERY_PARAMS.COMMON.SEARCH] as string,
    listType: queries[
      CommonConstants.QUERY_PARAMS.COMMON.LIST_TYPE
    ] as string[],
    dateStart: queries[
      CommonConstants.QUERY_PARAMS.COMMON.DATE_START
    ] as string,
    dateEnd: nextDayISOString(
      queries[CommonConstants.QUERY_PARAMS.COMMON.DATE_END] as string
    ),
    order: queries[CommonConstants.QUERY_PARAMS.COMMON.ORDER] as string,
    callStatus: queries[
      CommonConstants.QUERY_PARAMS.REPORT.CALL_STATUS
    ] as string[],
    disposition: queries[
      CommonConstants.QUERY_PARAMS.REPORT.DISPOSITION
    ] as string[],
    ...(isAdmin && {
      userIds: queries[
        CommonConstants.QUERY_PARAMS.REPORT.USER_IDS
      ] as string[],
    }),
    pageNo:
      (queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO] as number) - 1,
    pageSize: queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE] as number,
    timeZoneOffset: -new Date().getTimezoneOffset(), // we are sending a negative value of the offset as the BE expects the offset to be a certain way
    direction: queries[CommonConstants.QUERY_PARAMS.COMMON.DIRECTION] as string,
    voiceProvider: queries[
      CommonConstants.QUERY_PARAMS.REPORT.VOICE_PROVIDER
    ] as VoiceProviderTypes,
  };
};

/**
 * RETURNS ACTIVE SESSIONS RTK QUERY
 * @param search - URL QUERY PARAMS
 * @returns RTK Query parameters for active sessions
 */
export const getActiveSessionsRTKQuery = (
  search: string
): ActiveSessionsQuery => {
  const queries = query.parse(search);

  return {
    pageNo:
      (queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO] as number) - 1,
    pageSize: queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE] as number,
    order: queries[CommonConstants.QUERY_PARAMS.COMMON.ORDER] as string,
    sessionId: queries[
      CommonConstants.QUERY_PARAMS.REPORT.SESSION_ID
    ] as string[],
    search: queries[CommonConstants.QUERY_PARAMS.COMMON.SEARCH] as string,
    listType: queries[
      CommonConstants.QUERY_PARAMS.COMMON.LIST_TYPE
    ] as string[],
    dateStart: queries[
      CommonConstants.QUERY_PARAMS.COMMON.DATE_START
    ] as string,
    dateEnd: nextDayISOString(
      queries[CommonConstants.QUERY_PARAMS.COMMON.DATE_END] as string
    ),
    callStatus: queries[
      CommonConstants.QUERY_PARAMS.REPORT.CALL_STATUS
    ] as string[],
    disposition: queries[
      CommonConstants.QUERY_PARAMS.REPORT.DISPOSITION
    ] as string[],
    userIds: queries[CommonConstants.QUERY_PARAMS.REPORT.USER_IDS] as string[],
    direction: queries[CommonConstants.QUERY_PARAMS.COMMON.DIRECTION] as string,
    voiceProvider: queries[
      CommonConstants.QUERY_PARAMS.REPORT.VOICE_PROVIDER
    ] as VoiceProviderTypes,
  };
};
